import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { searchHotels, Hotel } from '../services/hotels';
import { useCurrency } from '../hooks/useCurrency';
import { useTranslation } from '../hooks/useTranslation';

const useQuery = () => {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
};

const SearchResults: React.FC = () => {
    const query = useQuery();
    const { formatPrice } = useCurrency();
    const { t } = useTranslation();
    const [hotels, setHotels] = useState<Hotel[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const city = query.get('city') || '';
    const name = query.get('hotel') || '';
    const checkIn = query.get('checkIn') || '';
    const checkOut = query.get('checkOut') || '';

    useEffect(() => {
        const run = async () => {
            setIsLoading(true);
            setError('');
            try {
                const results = await searchHotels({ city, name, checkIn, checkOut });
                setHotels(results);
            } catch (e: any) {
                setError(e?.message || 'Failed to load hotels');
            } finally {
                setIsLoading(false);
            }
        };
        run();
    }, [city, name, checkIn, checkOut]);

    return (
        <div className="container" style={{ paddingTop: 24, paddingBottom: 48 }}>
            <h1 style={{ marginBottom: 8 }}>{t('results.title')}</h1>
            <p style={{ marginBottom: 16, color: '#4b5563' }}>
                {city || name ? (
                    <>{t('results.showingIn')} {city ? <><b>{city}</b> </> : null}{name ? <>{t('results.matching')} <b>{name}</b></> : null}</>
                ) : (
                    <>{t('results.description')}</>
                )}
            </p>

            {isLoading && <div className="card" style={{ padding: 16 }}>{t('results.loading')}</div>}
            {error && <div className="card" style={{ padding: 16, color: 'var(--error-color)' }}>{error}</div>}

            {!isLoading && !error && hotels.length === 0 && (
                <div className="card" style={{ padding: 16 }}>{t('results.noResults')}</div>
            )}

            <div className="hotel-cards" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 }}>
                {hotels.map((h) => (
                    <div key={h.id} className="card" style={{ overflow: 'hidden' }}>
                        <img src={h.imageUrl} alt={h.name} style={{ width: '100%', height: 140, objectFit: 'cover' }} />
                        <div style={{ padding: 12 }}>
                            <h3 style={{ margin: '0 0 6px 0' }}>{h.name}</h3>
                            {h.address && <div style={{ color: '#6b7280', marginBottom: 6 }}>{h.address}</div>}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                {h.priceFrom ? <div style={{ fontWeight: 700 }}>{formatPrice(h.priceFrom)}</div> : <div />}
                                {h.rating ? <div style={{ color: '#f59e0b' }}>★ {h.rating}</div> : null}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default SearchResults;

